import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ReactMDEditor, {
  commands,
  ICommand,
  TextAreaCommandOrchestrator,
  TextAreaTextApi,
  TextRange,
  TextSection,
  TextState,
} from '@uiw/react-md-editor/nohighlight'
import clsx from 'clsx'
import rehypeExternalLinks from 'rehype-external-links'
import rehypeSanitize from 'rehype-sanitize'
import remarkBreaks from 'remark-breaks'

import {
  Bold16,
  Checkbox16,
  Code16,
  CodeBlock16,
  Hr16,
  Image16,
  Italic16,
  LineBreak16,
  Link16,
  Ol16,
  QuoteUp16,
  Strikethrough16,
  Ul16,
} from '@makyoapp/makyo-icon/icon'
import { Button, SimpleInput } from '@makyoapp/makyo-ui'

import { ImageManagerKind } from '@/context/ImageManagerContext'
import { ImageManager } from '@/components/ImageManager/ImageManager'

import '@uiw/react-markdown-preview/markdown.css'
import './Markdown.css'

function getSurroundingWord(text: string, position: number): TextRange {
  if (!text) throw Error("Argument 'text' should be truthy")

  const isWordDelimiter = (c: string) => c === ' ' || c.charCodeAt(0) === 10

  // leftIndex is initialized to 0 because if selection is 0, it won't even enter the iteration
  let start = 0
  // rightIndex is initialized to text.length because if selection is equal to text.length it won't even enter the interation
  let end = text.length

  // iterate to the left
  for (let i = position; i - 1 > -1; i--) {
    if (isWordDelimiter(text[i - 1])) {
      start = i
      break
    }
  }

  // iterate to the right
  for (let i = position; i < text.length; i++) {
    if (isWordDelimiter(text[i])) {
      end = i
      break
    }
  }

  return { start, end }
}

function selectWord({ text, selection }: TextSection): TextRange {
  if (text && text.length && selection.start === selection.end) {
    // the user is pointing to a word
    return getSurroundingWord(text, selection.start)
  }
  return selection
}

interface ChildrenWithHandle {
  handle: {
    close: () => void
    execute: () => void
    getState?: TextAreaCommandOrchestrator['getState']
    textApi?: TextAreaTextApi
  }
}

function MdeLink({ handle }: ChildrenWithHandle) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState('')
  const exec = useCallback(() => {
    const api = handle.textApi
    const state = handle.getState?.()
    if (state && api) {
      // Adjust the selection to encompass the whole word if the caret is inside one
      const newSelectionRange = selectWord({
        text: state?.text,
        selection: state?.selection,
      })

      const state1 = api?.setSelectionRange(newSelectionRange) as TextState
      const selectedText = state1.selectedText || value
      // Replaces the current selection with the bold mark up
      const state2 = api?.replaceSelection(`[${selectedText}](${value})`) as TextState

      // Adjust the selection to not contain the **
      api?.setSelectionRange({
        start: state2.selection.end - 6 - selectedText.length,
        end: state2.selection.end - 6,
      })
    }
  }, [value, handle])

  const handleSubmit = useCallback(() => {
    exec()
    setValue('')
    handle.close()
  }, [exec, handle, setValue])

  const [focus, setFocus] = useState(false)

  useEffect(() => {
    if (inputRef.current) {
      if (!inputRef.current.className.includes('focused')) {
        inputRef.current.focus()
      }
    }
  }, [inputRef])

  return (
    <div className='mk-mde-link'>
      <SimpleInput
        className={clsx(focus && 'focused')}
        ref={inputRef}
        tabIndex={1}
        autoFocus={true}
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value)
        }}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            handleSubmit()
          }
        }}
        outlined
      />

      <Button onClick={handleSubmit}>
        <Trans>Insert</Trans>
      </Button>
      {/*

         <Button variant={ButtonVariant.Ghost} onClick={() => handle.close()}>
         <Close12 />
         </Button>
         */}
    </div>
  )
}

function MdeImage({ handle }: ChildrenWithHandle) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as Element
      if (target.className?.includes?.('mk-mde-img') || target.className?.includes?.('mk-modal-bd')) {
        handle.close()
      }
    },
    [handle]
  )

  const handleChange = useCallback(
    (imageTemplate: string) => {
      const api = handle.textApi
      const state = handle.getState?.()
      if (api && state) {
        // Select everything
        const newSelectionRange = selectWord({
          text: state.text,
          selection: state.selection,
        })
        const state1 = api.setSelectionRange(newSelectionRange)
        // Replaces the current selection with the image
        //const imageTemplate =
        //  state1.selectedText || 'https://example.com/your-image.png'
        api.replaceSelection(`![${state1.selectedText}](${imageTemplate})`)
        // Adjust the selection to not contain the **
        api.setSelectionRange({
          start: 4 + state1.selection.start,
          end: 4 + state1.selection.start + imageTemplate.length,
        })
        handle.close()
      }
    },
    [handle]
  )
  return (
    <div className='mk-mde-img mk-modal-base' onClick={handleClick}>
      <div className='mk-modal-bd' onClick={handleClick} />
      <div className='mk-mde-img-inner mk-modal-inner modal-md'>
        <ImageManager
          id='mk-ag-mde-img'
          modal={false}
          onChange={(ty, v) => {
            switch (ty) {
              case ImageManagerKind.StockPhotos:
                handleChange(`${v}?q=50&w=720`)
                break
              case ImageManagerKind.Upload:
              case ImageManagerKind.ImageUrl:
              case ImageManagerKind.OpenAI:
              default:
                handleChange(v as string)
                break
            }
          }}
          onHide={() => {
            handle.close()
          }}
        />
      </div>
    </div>
  )
}

/**
 * Markdown Editor
 */
export default function MarkdownEditor(props: React.ComponentProps<typeof ReactMDEditor>) {
  const { t } = useTranslation()

  const extraCommands: ICommand[] = [
    {
      name: 'live',
      keyCommand: 'preview',
      value: 'live',
      buttonProps: {
        'aria-label': t('With Live Preview'),
        title: t('With Live Preview'),
        className: 'extra',
      },
      icon: <div>{t('Split')}</div>,
    },
    {
      name: 'edit',
      keyCommand: 'preview',
      value: 'edit',
      buttonProps: {
        'aria-label': t('Live Code'),
        title: t('Live Code'),
        className: 'extra',
      },
      icon: <div>{t('Markdown')}</div>,
    },

    {
      name: 'preview',
      keyCommand: 'preview',
      value: 'preview',
      buttonProps: {
        'aria-label': t('Preview Content'),
        title: t('Preview Content'),
        className: 'extra',
      },
      icon: <div>{t('Preview')}</div>,
    },
  ]

  const titleCmd = (text: string) => <span className='h-4 w-4 text-m-sm font-bold flex items-center'>{text}</span>

  const cmds = [
    {
      ...commands.bold,
      icon: <Bold16 />,
    },
    {
      ...commands.italic,
      icon: <Italic16 />,
    },
    {
      ...commands.strikethrough,
      icon: <Strikethrough16 />,
    },
    {
      ...commands.hr,
      icon: <Hr16 />,
    },
    {
      name: 'linebreak',
      keyCommand: 'linebreak',
      shortcuts: 'ctrl+enter',
      buttonProps: {
        'aria-label': 'Add line break (ctrl + enter)',
        title: 'Add line break (ctrl + enter)',
      },
      icon: <LineBreak16 />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        api.replaceSelection(`${state.selectedText}\\`)
      },
    },
    commands.group(
      [
        {
          ...commands.title1,
          icon: titleCmd('H1'),
        },
        {
          ...commands.title2,
          icon: titleCmd('H2'),
        },
        {
          ...commands.title3,
          icon: titleCmd('H3'),
        },
        {
          ...commands.title4,
          icon: titleCmd('H4'),
        },
        {
          ...commands.title5,
          icon: titleCmd('H5'),
        },
        {
          ...commands.title6,
          icon: titleCmd('H6'),
        },
      ],
      {
        name: 'heading',
        groupName: 'heading',
        buttonProps: {
          'aria-label': 'Insert heading',
          title: 'Insert heading',
        },
        icon: <div>H</div>,
      }
    ),
    commands.divider,
    commands.group([], {
      name: 'link',
      groupName: 'link',
      icon: <Link16 />,
      children: (handle) => {
        return <MdeLink handle={handle} />
      },
      buttonProps: { 'aria-label': 'Insert link', title: 'Insert link' },
    }),
    {
      ...commands.quote,
      icon: <QuoteUp16 />,
    },
    {
      ...commands.code,
      icon: <Code16 />,
    },
    {
      ...commands.codeBlock,
      icon: <CodeBlock16 />,
    },

    commands.group([], {
      name: 'gallery',
      groupName: 'gallery',
      icon: <Image16 width={16} height={16} />,
      children: (handle) => {
        return <MdeImage handle={handle} />
      },
      buttonProps: { 'aria-label': 'Insert image', title: 'Insert image' },
    }),
    commands.divider,
    {
      ...commands.unorderedListCommand,
      icon: <Ul16 />,
    },
    {
      ...commands.orderedListCommand,
      icon: <Ol16 />,
    },
    {
      ...commands.checkedListCommand,
      icon: <Checkbox16 />,
      execute: (state: TextState, api: TextAreaTextApi) => {
        api.replaceSelection(`- [x] ${state.selectedText}`)
      },
    },
  ]

  const previewRef = useRef<{ mdp: RefObject<HTMLDivElement> }>(null)

  useEffect(() => {
    const cb = (e: Event) => {
      e.stopPropagation()
    }
    const { current } = previewRef

    const parent = current?.mdp.current?.closest('.w-md-editor-preview')
    parent?.addEventListener('click', cb)
    return () => {
      parent?.removeEventListener('click', cb)
    }
  }, [previewRef])

  return (
    <ReactMDEditor
      autoFocus={true}
      value={props.value}
      onChange={props.onChange}
      fullscreen={false}
      className={props.className}
      height={props.height}
      commands={cmds}
      extraCommands={extraCommands}
      visibleDragbar={false}
      textareaProps={{
        onClick: (e) => e.stopPropagation(),
      }}
      previewOptions={
        {
          ref: previewRef,
          rehypePlugins: [
            [rehypeSanitize],
            [rehypeExternalLinks, { target: '_blank', rel: ['nofollow', 'noopener', 'noreferrer'] }],
          ],
          remarkPlugins: [remarkBreaks],
        } as never
      }
    />
  )
}
